import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Dropdown, Modal } from 'react-bootstrap';
import classNames from 'classnames/bind';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckSquare } from '@fortawesome/pro-solid-svg-icons/faCheckSquare';
import { faSquare } from '@fortawesome/pro-light-svg-icons/faSquare';
import { faChevronUp } from '@fortawesome/pro-light-svg-icons/faChevronUp';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons/faChevronDown';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import Tooltip from '../../Tooltip';

import useSizes from '../../../hooks/sizes';

import Styles from './styles.module.scss';

const cx = classNames.bind(Styles);

const tooltipContentStyle = {
  transform: 'translate(calc(-50% + 8px), calc(-100% - 10px))',
  fontWeight: 'normal',
  textTransform: 'none',
  width: '218px',
  height: '110px',
  zIndex: 1051,
};

const Content = ({
  label,
  multipleSelect,
  toggleAllSelected,
  allSelected,
  options,
  onValueSelect,
  tooltip,
}) => (
  <>
    {label && (
      <div className={Styles.menu_title}>
        {label}
        :
        {tooltip && (
          <Tooltip
            className={Styles.tooltip}
            tooltipClassName={Styles['tooltip-content']}
            renderOutside
            hideWithOverflow
            contentStyle={tooltipContentStyle}
            dynamicPosition
          >
            {tooltip}
          </Tooltip>
        )}
      </div>
    )}
    <div className={Styles.menu_content}>
      {multipleSelect && (
        <Dropdown.Item
          onSelect={toggleAllSelected}
        >
          <FontAwesomeIcon className={allSelected ? 'active' : ''} icon={allSelected ? faCheckSquare : faSquare} />
          Select / Unselect All
        </Dropdown.Item>
      )}
      {options.map((option) => (
        <Dropdown.Item
          key={option.value}
          eventKey={option.value}
          active={!multipleSelect && option.selected}
          onSelect={onValueSelect}
        >
          {multipleSelect && (
            <FontAwesomeIcon className={option.selected ? 'active' : ''} icon={option.selected ? faCheckSquare : faSquare} />
          )}
          {option.label}
        </Dropdown.Item>
      ))}
    </div>
  </>
);

export const CFDropdown = ({
  onSelect,
  options: initialOptions,
  label,
  className = '',
  multipleSelect = false,
  allSelectedLabel = '',
  useMobileModal = true,
  useMobileView = false,
  hideLabelInTitle = false,
  tooltip = '',
}) => {
  const [options, setOptions] = useState([]);
  const [opened, setOpened] = useState(false);
  const { width } = useSizes();

  const dropdownId = useRef(Math.random().toString().split('.')[1]);

  const allSelected = useMemo(() => (
    options.every((option) => option.selected)
  ), [options]);

  const title = useMemo(() => (
    !allSelected ? (
      options
        .filter((option) => option.selected)
        .map((option) => option.label)
        .join(',') || `Select ${label}`
    ) : (
      allSelectedLabel || `All ${label}`
    )
  ), [allSelected, allSelectedLabel, options, label]);

  useEffect(() => {
    const newOptions = initialOptions.map((option) => ({
      value: option.value,
      label: option.label,
      selected: Boolean(option.selected),
    }));

    if (newOptions.length && !multipleSelect && !newOptions.some((option) => option.selected)) {
      newOptions[0].selected = true;
    }

    setOptions(newOptions);
    if (onSelect) {
      const selectedValues = newOptions
        .filter((option) => option.selected)
        .map((option) => option.value);

      setTimeout(() => {
        if (multipleSelect) {
          onSelect(selectedValues);
        } else if (selectedValues.length) {
          onSelect(selectedValues[0]);
        }
      }, 1);
    }
  }, [initialOptions]);

  const onToggle = useCallback((isOpen, event, metadata) => {
    if (metadata?.source === 'select' && multipleSelect) {
      return;
    }

    setOpened((opened) => !opened);
  }, [multipleSelect]);

  const toggleAllSelected = useCallback(() => {
    setOptions((currentOptions) => {
      const newOptions = currentOptions.map((option) => ({
        ...option,
        selected: !allSelected,
      }));

      if (onSelect) {
        setTimeout(() => {
          onSelect(!allSelected ? currentOptions.map((option) => option.value) : []);
        }, 1);
      }

      return newOptions;
    });
  }, [onSelect, allSelected]);

  const onValueSelect = useCallback((eventKey) => {
    setOptions((currentOptions) => {
      const selectedValues = [];

      const newOptions = currentOptions.map((option) => {
        const selectedItem = eventKey === option.value;
        let { selected } = option;

        if (multipleSelect) {
          if (selectedItem) {
            selected = !selected;
          }
        } else {
          selected = selectedItem;
        }

        if (selected) {
          selectedValues.push(option.value);
        }

        return {
          ...option,
          selected,
        };
      });

      if (onSelect) {
        setTimeout(() => {
          if (multipleSelect) {
            onSelect(selectedValues);
          } else if (selectedValues.length) {
            onSelect(selectedValues[0]);
          }
        }, 1);
      }

      return newOptions;
    });
  }, [onSelect, multipleSelect]);

  return (
    <Dropdown
      id={`cf_dropdown_${dropdownId.current}`}
      className={cx('cf_dropdown', className, { 'mobile-view': useMobileView })}
      onToggle={onToggle}
      show={opened}
    >
      <Dropdown.Toggle>
        <div className={Styles.title}>
          <span>{title}</span>
          {!hideLabelInTitle && (
            <div className={Styles.label}>
              {label}
            </div>
          )}
        </div>
        <div className={Styles.arrow}>
          <FontAwesomeIcon icon={opened ? faChevronUp : faChevronDown} />
        </div>
      </Dropdown.Toggle>
      {(!useMobileModal || width > 1024) ? (
        <Dropdown.Menu
          rootCloseEvent="click"
          flip={false}
          show={opened}
        >
          <Content
            label={label}
            multipleSelect={multipleSelect}
            toggleAllSelected={toggleAllSelected}
            allSelected={allSelected}
            options={options}
            onValueSelect={onValueSelect}
            tooltip={tooltip}
          />
        </Dropdown.Menu>
      ) : (
        <Modal
          show={opened}
          onHide={onToggle}
          dialogClassName={cx('cf_dropdown', 'modal')}
          centered
        >
          <FontAwesomeIcon
            icon={faXmark}
            onClick={onToggle}
            className={cx('close')}
          />
          <Content
            label={label}
            multipleSelect={multipleSelect}
            toggleAllSelected={toggleAllSelected}
            allSelected={allSelected}
            options={options}
            onValueSelect={onValueSelect}
            tooltip={tooltip}
          />
        </Modal>
      )}
    </Dropdown>
  );
};

export default memo(CFDropdown);
